import { useEffect, useState, useCallback } from "react";
import { usePwaInstall } from "../hooks/usePwaInstall";
import { InstallHelpIos } from "./InstallHelpIos";

const STORAGE_DISMISSED_KEY = "virtus_install_prompt_dismissed_at";
const SHOW_AGAIN_AFTER_DAYS = 14;

function shouldShowAfterDismiss(): boolean {
  if (typeof localStorage === "undefined") return true;
  const raw = localStorage.getItem(STORAGE_DISMISSED_KEY);
  if (!raw) return true;
  const dismissedAt = parseInt(raw, 10);
  if (!Number.isFinite(dismissedAt)) return true;
  const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
  return daysSince >= SHOW_AGAIN_AFTER_DAYS;
}

interface InstallPromptProps {
  /**
   * Suprime o card flutuante (ex.: na home, onde o hero embutido em
   * `ModuleHome` já orienta a instalação — evita aviso duplicado).
   */
  suppressed?: boolean;
}

export function InstallPrompt({ suppressed = false }: InstallPromptProps) {
  const { isIos, isStandalone, canInstallNatively, promptInstall } = usePwaInstall();
  const [visible, setVisible] = useState<boolean>(false);
  const [iosHelpOpen, setIosHelpOpen] = useState<boolean>(false);

  // Android/desktop Chromium: revela o card assim que o prompt nativo é capturado.
  useEffect(() => {
    if (isStandalone || !canInstallNatively) return;
    if (!shouldShowAfterDismiss()) return;
    setVisible(true);
  }, [isStandalone, canInstallNatively]);

  // iOS Safari: nunca dispara beforeinstallprompt. Mostra prompt manual
  // após pequeno delay para não atrapalhar primeiro paint.
  useEffect(() => {
    if (isStandalone || !isIos) return;
    if (!shouldShowAfterDismiss()) return;
    const iosTimer = window.setTimeout(() => setVisible(true), 3500);
    return () => window.clearTimeout(iosTimer);
  }, [isStandalone, isIos]);

  const handleInstall = useCallback(async () => {
    const outcome = await promptInstall();
    if (outcome === "accepted") setVisible(false);
  }, [promptInstall]);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    setIosHelpOpen(false);
    try {
      localStorage.setItem(STORAGE_DISMISSED_KEY, String(Date.now()));
    } catch {
      /* ignore (modo privado pode bloquear localStorage) */
    }
  }, []);

  if (suppressed || isStandalone || !visible) return null;

  return (
    <>
      <div className="install-prompt" role="dialog" aria-labelledby="install-prompt-title">
        <div className="install-prompt__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 4v10" />
            <path d="M8 10l4 4 4-4" />
            <path d="M4 18h16" />
          </svg>
        </div>
        <div className="install-prompt__copy">
          <strong id="install-prompt-title" className="install-prompt__title">
            Adicionar à tela inicial
          </strong>
          <span className="install-prompt__lead">
            {isIos
              ? "Use o Manual como app nativo no seu iPhone/iPad."
              : "Instale como app — abre em janela própria, sem barra do navegador."}
          </span>
        </div>
        <div className="install-prompt__actions">
          {canInstallNatively ? (
            <button type="button" className="install-prompt__cta" onClick={handleInstall}>
              Instalar
            </button>
          ) : isIos ? (
            <button
              type="button"
              className="install-prompt__cta"
              onClick={() => setIosHelpOpen(true)}
            >
              Como instalar
            </button>
          ) : null}
          <button
            type="button"
            className="install-prompt__dismiss"
            onClick={handleDismiss}
            aria-label="Fechar sugestão de instalação"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M6 6l12 12" />
              <path d="M18 6l-12 12" />
            </svg>
          </button>
        </div>
      </div>

      {iosHelpOpen ? <InstallHelpIos onClose={() => setIosHelpOpen(false)} /> : null}
    </>
  );
}
